import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppFrameWide, IFrameWideRoot } from "./AppFrameWide";
import { AppIcon, AppIcons } from "./AppIcons";

// AppPhoneChat - an open chat on a phone gives the CONVERSATION the screen (owner 2026-11-01: "The actual chat must take up
// at least 70% of the space even if the keyboard is visible"). Outside the frame (app.css block `=== wac-phonechat ===`):
// no app top bar, no property row, no bottom rail, a one-line header, a one-row composer. Inside the frame (`FrameCss`,
// in `AppMain.Frame`'s sheet list): the page's Details / Insights row is tight and the task strip at most two rows.
// ⭐ While the on-screen keyboard is open (the shared kViewportHeightVar's keyboard class `wac-kb` on the app's <html>, and
// `data-wac-kb` on the frame's <html> - `Apply`), the page's header row and the task strip step aside too.
// ⚠ Habitat-free (the Bridge typecheck has no DOM lib): the frame's root is the `AppFrameWide` port.

export class AppPhoneChat {
    /** The class the shared kViewportHeightVar puts on the app's <html> while the keyboard is open. */
    public static readonly KeyboardClass: string = "wac-kb";
    /** The frame's <html> attribute while the keyboard is open. */
    public static readonly FrameAttr: string = "data-wac-kb";

    /** The frame's <html> attribute while the task sheet is open (`AppPhoneChatRun`). */
    public static readonly SheetAttr: string = "data-wac-tasksheet";
    /** The header buttons' attribute: tasks | details | insights. */
    public static readonly PageAttr: string = "data-wac-pagebtn";

    /**
     * The frame's phone rules (a narrow frame that is not in the wide layout). DOCUMENTED EXCEPTION: constant text.
     * ⭐ (owner 2026-11-01: "Move the tasks into a dialog also, like details and insights") the page's own header row
     * (count, Details, Insights) and the task strip are GONE on a phone: the app's chat header carries the three buttons
     * (`HeadButtons`), Details / Insights open their drawers, Tasks opens the strip as a bottom sheet.
     */
    public static FrameCss = (): string => {
        const Phone: string = "html[data-wac-embedded]" + AppFrameWide.Not;
        const Sheet: string = Phone + "[" + AppPhoneChat.SheetAttr + "]";
        // ⛔ The sheet's head (title + close) is hidden OUTSIDE the phone query too (owner 2026-11-02: "The X is too large"
        // - on a desktop it stood unstyled above the strip, a bare 20px button). It shows only as the open sheet's head.
        return "html[data-wac-embedded] .wac-tshead,html[data-wac-embedded] .wac-tsback{display:none}"
            + "@media (max-width:1099px){"
            + Phone + " header.bar," + Phone + " .tasks{display:none!important}"
            + Sheet + " .tasks{display:block!important;position:fixed;left:0;right:0;bottom:0;max-height:75vh;overflow-y:auto;"
            + "z-index:40;background:var(--inbg,#fff);border-radius:12px 12px 0 0;box-shadow:0 -4px 24px rgba(0,0,0,.25);padding:4px 12px 16px}"
            + Sheet + " .tasks .tmore{display:none}"
            + Phone + " .wac-tshead," + Phone + " .wac-tsback{display:none}"
            + Sheet + " .wac-tshead{display:flex;align-items:center;justify-content:space-between;font-weight:600;padding:8px 0}"
            + Sheet + " .wac-tshead button{min-width:44px;min-height:44px;border:0;background:none;font:inherit;font-size:22px;cursor:pointer;color:inherit}"
            + Sheet + " .wac-tsback{display:block;position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:39}"
            + "}";
    };

    /** The three buttons in the app's chat header (shown only below 1100px; hidden until the page has the target). */
    public static HeadButtons = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<span class=\"chpage\">"
                + AppPhoneChat.Button("tasks", "Tasks", AppIcons.NavTasks)
                + AppPhoneChat.Button("details", "Details", AppIcons.Person)
                + AppPhoneChat.Button("insights", "Insights", AppIcons.NavOverview)
                // ⭐ (owner review 2026-11-01) a task is added for THIS chat on a phone - the Tasks tab has no generic row there.
                + AppPhoneChat.Button("addtask", "Add task", AppIcons.Plus) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChat.HeadButtons");
            return "";
        }
    };

    // DOCUMENTED EXCEPTION: pure markup (the caller guards).
    private static Button = (Kind: string, Words: string, Icon: AppIcon): string => {
        return "<button type=\"button\" class=\"btn ghost icon sm chpagebtn\" " + AppPhoneChat.PageAttr + "=\"" + Kind
            + "\" aria-label=\"" + Words + "\" title=\"" + Words + "\" hidden>" + AppIcons.Svg(Icon, 20)
            + "<i class=\"chpagecount\"></i></button>";
    };

    /** The keyboard opened / closed: the open chat's frame follows. */
    public static Apply = (Root: IFrameWideRoot | null, Open: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Root === null) { return; }
            // ⭐ The keyboard closes the task sheet (seen on the emulator: the sheet stayed over the conversation while typing).
            if (Open === true) { Root.setAttribute(AppPhoneChat.FrameAttr, "1"); Root.removeAttribute(AppPhoneChat.SheetAttr); return; }
            Root.removeAttribute(AppPhoneChat.FrameAttr);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneChat.Apply");
        }
    };
}
